import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAccount, useDisconnect, useNetwork, useSwitchNetwork } from "wagmi";
import EthCrypto from "eth-crypto";
import Web3 from "web3";
import moment from "moment";

import {FriendsList} from "./FriendsList";
import MessageSender from "./MessageSender";
import {ChatBox} from "./ChatBox";

import {
  CONTRACT_META_DATA,
  BURNER_ADDRESS,
  GQL_QUERY_IDENTITY_TIMESTAMP_RECENT,
  GQL_QUERY_MESSAGE_LOG_INTERVAL,
  GQL_QUERY_MESSAGE_LOG_INIT,
} from "../../constants";

import EchoJSON from "../../contracts/Echo.json";
import {
  logoutIconSVG,
  textBubbleSVG,
  dropdownIconSVG,
  echoooLogoSVG,
  errorIconSVG,
  changeKeysIconSVG,
  profileIconSVG,
} from "../../assets";
import { ContractInstance, useTheGraphClient } from "../../hooks";
import "./receivers.css";

// TODO: using both web3 and ethers... needs some thought

const decryptMessage = async (senderPrivateKey, message) => {
  const decryptedMessage = await EthCrypto.decryptWithPrivateKey(
    senderPrivateKey,
    EthCrypto.cipher.parse(message)
  ).catch((err) => {
    console.log("Sending Message Error:", err);
    let errorMessage = "Error: Encryption error. Change communication keys";
    return errorMessage;
  });
  return decryptedMessage;
};

const parseJSONMessage = async (messageType, decryptedMessage) => {
  if (messageType === "1" || messageType === "2") {
    decryptedMessage = JSON.parse(decryptedMessage);
  }
  return decryptedMessage;
};

const selectEncryptedMessage = (messageMetadata, senderAddress) => {
  let encryptedMessage;
  if (messageMetadata.from === senderAddress) {
    // Sender message
    encryptedMessage = messageMetadata.senderMessage;
  } else {
    // Receiver message
    encryptedMessage = messageMetadata.receiverMessage;
  }
  return encryptedMessage;
};

const getMessage = async (messageMetadata, senderAddress, senderPrivateKey) => {
  const encryptedMessage = await selectEncryptedMessage(
    messageMetadata,
    senderAddress
  );
  let decryptedMessage = await decryptMessage(
    senderPrivateKey,
    encryptedMessage
  );
  decryptedMessage = await parseJSONMessage(
    messageMetadata.messageType,
    decryptedMessage
  );
  return decryptedMessage;
};

const getNewMessageLog = async (
  messageData,
  senderAddress,
  senderPrivateKey
) => {
  const messageLog = messageData;
  let messageMetadata;
  let decryptedMessage;
  let isValidMessage;

  for (let idx = 0; idx < messageData.length; idx++) {
    messageMetadata = await messageData[idx];

    // check that encrypted message is correct
    isValidMessage = isValidEncryptedMessage(
      messageMetadata.senderMessage,
      messageMetadata.receiverMessage
    );
    if (!isValidMessage) {
      continue;
    }

    decryptedMessage = await getMessage(
      messageMetadata,
      senderAddress,
      senderPrivateKey
    );

    messageLog[idx].message = decryptedMessage;
    messageLog[idx].messageType = messageMetadata.messageType;
  }
  return messageLog;
};

const isValidEncryptedMessage = (senderMessage, receiverMessage) => {
  // check that encrypted message is correct
  if (senderMessage.length < 194 || receiverMessage.length < 194) {
    return false;
  }
  return true;
};

const intervalFetchMessages = async (
  address,
  newMessage,
  activeReceiverAddress,
  setMessageLog,
  graphClient,
  senderPrivateKey
) => {
  let recentTimestamp;
  const senderAddress = address.toLowerCase();
  const recentMessage = newMessage[activeReceiverAddress];

  console.log("interval fetch messages >>>");
  if (recentMessage == null || recentMessage.length === 0) {
    return;
  } else {
    recentTimestamp = recentMessage.at(-1).timestamp;
  }

  const dataMessages = await graphClient
    .query(GQL_QUERY_MESSAGE_LOG_INTERVAL, {
      senderAddress: senderAddress,
      receiverAddress: activeReceiverAddress,
      recentMessageTimestamp: recentTimestamp,
    })
    .toPromise();

  const messageData = dataMessages.data.messages;

  if (Object.keys(messageData).length === 0 || messageData == null) {
    return;
  }
  const messageLog = await getNewMessageLog(
    messageData,
    senderAddress,
    senderPrivateKey
  );
  const newMessageLog = {
    ...newMessage,
    [activeReceiverAddress]: messageLog,
  };
  setMessageLog(newMessageLog);
};

export default function MessagingPage({
  toggleOpenModalChainSelect,
  toggleOpenCommAddressModal,
  toggleOpenNewChatModal,
  toggleOpenProfileModal,
  toggleOpenNFTOfferModal,
  toggleOpenSendModal,
  toggleOpenLendingOfferModal,
  chatAddresses,
  activeReceiverAddress,
  setActiveReceiver,
  activeIndex,
  setActiveIndex,
  communicationAddress,
  setChatAddresses,
  messagesState,
  setMessagesState,
  messages,
  setMessageLog,
}) {
  const { address } = useAccount();
  const { disconnect } = useDisconnect();
  const { chain } = useNetwork();
  const { chains } = useSwitchNetwork();

  const [unknownChatAddresses, setUnknownChatAddresses] = useState({});
  const [openP2P, setOpenP2P] = useState(false);
  const [showTrustedAddressList, setShowTrustedAddressList] = useState(true);
  const [profilePicture, setProfilePicture] = useState("");

  const messagesRef = useRef(messages);
  const wsRef = useRef(null);
  const web3Ref = useRef(null);

  const contracts = ContractInstance();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const graphClient = useTheGraphClient();

  useEffect(() => {
    messagesRef.current = messages;
    async function fetchProfilePicture() {
      setProfilePicture(await getPFP(address));
    }
    fetchProfilePicture();
  });

  const getPFP = async (userAddress) => {
    return await contracts.contractPFP.getProfilePicture(userAddress);
  };

  const intervalCallback = useCallback(
    (
      address,
      newMessage,
      activeReceiverAddress,
      setMessageLog,
      graphClient,
      senderPrivateKey
    ) => {
      intervalFetchMessages(
        address,
        newMessage,
        activeReceiverAddress,
        setMessageLog,
        graphClient,
        senderPrivateKey
      );
    },
    [activeReceiverAddress]
  );

  const fetchMessagesAsyncCallback = useCallback(async () => {
    const senderAddress = address.toLowerCase();
    let senderPrivateKey = JSON.parse(
      localStorage.getItem("private-communication-address")
    );
    senderPrivateKey = senderPrivateKey[address][chain.id];

    if (activeReceiverAddress != null) {
      const dataIdentity = await graphClient
        .query(GQL_QUERY_IDENTITY_TIMESTAMP_RECENT, {
          senderAddress: senderAddress,
        })
        .toPromise();
      const dataIdentityTimestamp = dataIdentity.data.identities[0].timestamp;

      const dataMessages = await graphClient
        .query(GQL_QUERY_MESSAGE_LOG_INIT, {
          senderAddress: senderAddress,
          receiverAddress: activeReceiverAddress,
          recentTimestamp: dataIdentityTimestamp,
        })
        .toPromise();

      const messageData = dataMessages.data.messages;
      const messageLog = await getNewMessageLog(
        messageData,
        senderAddress,
        senderPrivateKey
      );

      const newMessageLog = {
        ...messages,
        [activeReceiverAddress]: messageLog,
      };
      setMessageLog(newMessageLog);
      return newMessageLog;
    }
    // Do nothing is conditions are not met
    return;
  }, [activeReceiverAddress]);

  // init websocket and change provider when switching chains
  useEffect(() => {
    let wsCurrent = wsRef.current;
    let web3Current = web3Ref.current;
    let wsURL;
    if (chain.id in CONTRACT_META_DATA) {
      wsURL = CONTRACT_META_DATA[chain.id].quickNodeWSURL;
      // clean up current websocket
      if (wsCurrent != null) {
        web3Current.eth.clearSubscriptions();
        wsCurrent.unsubscribe((error, success) => {
          if (success) {
            console.log("ws: unsubscribed!");
          }
        });
      } else {
        web3Current = new Web3(wsURL);
        web3Ref.current = web3Current;
      }
      web3Current.setProvider(wsURL);
    }
  }, [chain]);

  // fetch messages and connect websocket
  useEffect(() => {
    let senderPrivateKey = JSON.parse(
      localStorage.getItem("private-communication-address")
    );

    // if activeReceiver is default address
    if (activeReceiverAddress === BURNER_ADDRESS) {
      return;
    }

    // if the sender has not created a private key do not fetch messages
    if (
      senderPrivateKey == null ||
      senderPrivateKey[address] == null ||
      senderPrivateKey[address][chain.id] == null
    ) {
      return;
    }
    senderPrivateKey = senderPrivateKey[address][chain.id];

    // unsupported chain
    if (CONTRACT_META_DATA[chain.id] == null) {
      return;
    }

    let wsCurrent = null;
    let interval = null;
    let web3Current = web3Ref.current;

    if (activeReceiverAddress !== "") {
      fetchMessagesAsyncCallback();

      const abi = EchoJSON.abi;
      const eventABI = abi.find((item) => {
        return item.name === "MessageEvent";
      });

      wsRef.current = web3Current.eth
        .subscribe(
          "logs",
          {
            address: contracts.contractEcho.address,
            topics: [eventABI.signature],
          },
          (error, result) => {
            if (error) {
              console.log("ws subscription error:", error);
              // Long poll instead of using websockets
              interval = setInterval(
                async (
                  senderAddress,
                  newMessage,
                  activeReceiverAddress,
                  setMessageLog,
                  graphClient,
                  senderPrivateKey
                ) =>
                  intervalCallback(
                    senderAddress,
                    newMessage,
                    activeReceiverAddress,
                    setMessageLog,
                    graphClient,
                    senderPrivateKey
                  ),
                5 * 1000,
                address,
                messagesRef.current,
                activeReceiverAddress,
                setMessageLog,
                graphClient,
                senderPrivateKey
              );
            }
          }
        )
        .on("connected", (subscriptionId) => {
          console.log("ws connected id: ", subscriptionId);
        })
        .on("data", (log) => {
          const addNewMessage = async () => {
            let encryptedMessage;
            let decryptedMessage;

            // remove the anon topic
            const topicsSliced = log.topics.slice(1, log.topics.length);

            const senderLogAddress = web3Current.eth.abi
              .decodeParameter("address", topicsSliced[1])
              .toLowerCase();

            const receiverLogAddress = web3Current.eth.abi
              .decodeParameter("address", topicsSliced[0])
              .toLowerCase();

            const senderAddress = address.toLowerCase();
            const receiverAddress = activeReceiverAddress.toLowerCase();

            // ensure that address is from the sender or receiver
            // send the message can be from either sender or receiver perspective we need to check both cases
            const knownAddresses = [senderAddress, receiverAddress];
            if (
              !knownAddresses.includes(senderLogAddress) ||
              !knownAddresses.includes(receiverLogAddress)
            ) {
              console.log("ws data: non matching addresses");
              return;
            }

            const messageEvent = web3Current.eth.abi.decodeLog(
              eventABI.inputs,
              log.data,
              topicsSliced
            );

            // check that encrypted message is correct
            const isValidMessage = isValidEncryptedMessage(
              messageEvent._senderMessage,
              messageEvent._receiverMessage
            );
            if (!isValidMessage) {
              return;
            }

            if (senderAddress === senderLogAddress) {
              encryptedMessage = messageEvent._senderMessage;
            } else {
              encryptedMessage = messageEvent._receiverMessage;
            }

            decryptedMessage = await decryptMessage(
              senderPrivateKey,
              encryptedMessage
            );

            decryptedMessage = await parseJSONMessage(
              messageEvent._messageType,
              decryptedMessage
            );

            const newMessage = messagesRef.current;
            const messageLog = newMessage[activeReceiverAddress];
            const messageFrom =
              senderLogAddress === senderAddress
                ? senderAddress
                : receiverAddress;
            const newMessageData = {
              from: messageFrom,
              message: decryptedMessage,
              messageType: messageEvent._messageType,
              receiverMessage: messageEvent._receiverMessage,
              senderMessage: messageEvent._senderMessage,
              timestamp: `${moment().unix()}`,
            };

            messageLog.push(newMessageData);
            const newMessageLog = {
              ...newMessage,
              [activeReceiverAddress]: messageLog,
            };
            setMessageLog(newMessageLog);
          };
          addNewMessage();
        })
        .on("error", (error) => {
          console.log("ws error:", error);
          fetchMessagesAsyncCallback();
        })
        .on("changed", (syncing) => {
          console.log("ws change ", syncing);
        });

      wsCurrent = wsRef.current;
    }
    return () => {
      if (interval != null) {
        clearInterval(interval);
      }

      if (wsCurrent == null) {
        return;
      }

      wsCurrent.unsubscribe((error, success) => {
        if (success) {
          console.log("ws unsubscribe: unsubscribed");
        }
        if (interval != null) {
          clearInterval(interval);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReceiverAddress]);

  // TODO: Break this up into a bunch of components, way too much code here
  // - could do left column, header, and right column as a component
  return (
    <div
      className="flex flex-row h-[100vh] w-[100vw] bg-gradient-bg"
      style={{ backgroundRepeat: "round" }}
    >
      <FriendsList
        chatAddresses={chatAddresses}
        setChatAddresses={setChatAddresses}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        setActiveReceiver={setActiveReceiver}
        unknownChatAddresses={unknownChatAddresses}
        setUnknownChatAddresses={setUnknownChatAddresses}
        showTrustedAddressList={showTrustedAddressList}
        setShowTrustedAddressList={setShowTrustedAddressList}
      />
      <div className="w-[70%] flex flex-col justify-between">
        <div>
          <div className="flex flex-row justify-between items-center px-[20px] py-[40px] h-[100px]">
            <img className="h-[30px]" src={echoooLogoSVG} alt=""></img>
            <div className="flex flex-row gap-4">
              <button
                className="flex flex-row justify-center items-center gap-[15px] px-5 py-3 bg-white text-black font-bold rounded-[30px]"
                onClick={toggleOpenModalChainSelect}
              >
                {!chains.map((value) => value.id).includes(chain.id) ? (
                  <>
                    <img className="w-[25px]" src={errorIconSVG} alt=""></img>
                    Unsupported Chain
                    <img src={dropdownIconSVG} alt=""></img>
                  </>
                ) : (
                  <>
                    <img
                      className="w-[30px]"
                      src={CONTRACT_META_DATA[chain.id].logo}
                      alt=""
                    ></img>
                    {CONTRACT_META_DATA[chain.id].name}
                    <img src={dropdownIconSVG} alt=""></img>
                  </>
                )}
              </button>
              <button
                className="flex flex-row justify-center items-center gap-[10px] px-5 py-3 bg-white text-black font-bold rounded-[30px]"
                onClick={() => disconnect()}
              >
                {`${address.substring(0, 4)}...${address.substring(38)}`}
                <img src={logoutIconSVG} alt=""></img>
              </button>
              {chain.id in CONTRACT_META_DATA ? (
                <button
                  className="flex flex-row justify-center items-center gap-[10px] px-5 py-3 bg-[rgb(44,157,218)] text-white font-bold rounded-[30px]"
                  onClick={toggleOpenCommAddressModal}
                >
                  Update keys
                  <img
                    className="h-[20px] w-[20px]"
                    src={changeKeysIconSVG}
                    alt=""
                  ></img>
                </button>
              ) : (
                <></>
              )}
              {communicationAddress && chain.id in CONTRACT_META_DATA ? (
                <>
                  <button
                    className="flex flex-row justify-center items-center gap-[15px] px-5 py-3 bg-gradient-to-r from-[#00FFD1] to-[#FF007A] via-[#9b649c] text-white font-bold rounded-[30px]"
                    onClick={toggleOpenNewChatModal}
                  >
                    New chat
                    <img src={textBubbleSVG} alt=""></img>
                  </button>
                  <button
                    className="flex justify-center items-center w-[54px] bg-white rounded-[30px]"
                    onClick={() => {
                      toggleOpenProfileModal();
                    }}
                  >
                    {Boolean(profilePicture) ? (
                      <img
                        className="rounded-[30px] h-[54px]"
                        src={profilePicture}
                        alt=""
                      ></img>
                    ) : (
                      <img src={profileIconSVG} alt=""></img>
                    )}
                  </button>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
          {/* Receiver Address */}
          {(address in chatAddresses && chatAddresses[address].length > 0) ||
          (address in unknownChatAddresses &&
            unknownChatAddresses[address].length > 0) ? (
            <div className="w-full" style={{ height: "calc(5vh - 100px}" }}>
              <div className="flex justify-center align-center">
                <div className="shadow-md flex flex-wrap rounded-[10px] border-[1px] p-5 bg-[rgba(255,255,255,0.45)] text-center text-md break-words">
                  {activeReceiverAddress}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>

        {/* Chat */}
        {(address in chatAddresses && chatAddresses[address].length > 0) ||
        (address in unknownChatAddresses &&
          unknownChatAddresses[address].length > 0) ? (
          <div className="relative flex flex-col justify-end h-full">
            <ChatBox
              messages={messages}
              setMessageLog={setMessageLog}
              activeReceiverAddress={activeReceiverAddress}
            />
            <MessageSender
              messages={messages}
              setMessageLog={setMessageLog}
              activeReceiverAddress={activeReceiverAddress}
              messagesState={messagesState}
              setMessagesState={setMessagesState}
              toggleOpenSendModal={toggleOpenSendModal}
              toggleOpenNFTOfferModal={toggleOpenNFTOfferModal}
              toggleOpenLendingOfferModal={toggleOpenLendingOfferModal}
              openP2P={openP2P}
              setOpenP2P={setOpenP2P}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
